import React, { useContext, useEffect, useState } from 'react';
import { BaseComponentProps } from '@/pages/benchmark/types/commom';
import classNames from 'classnames';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import { useIntl } from 'umi';
import { LANGUAGE_EN, SYSTEM_THEME_DARK } from '@home/_config/config';
import { getName } from '@home/utils/common';
import CommonBubble from '@home/components/CommonBubble';
import { ThemeContext } from '@home/wrappers/common';
import {
  getStudioDescDom,
  getStudioMoreScoreDom,
  getStudioScoreDom,
} from './getRender';
import SvgIcon from '@home/components/SvgIcon';
import { getGameHeader } from '@/pages/dashboard/pages/benchmark/gtdrAssessment/components/GTDRTableCard/getRender';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import GTDRTable from '@/pages/dashboard/pages/benchmark/gtdrAssessment/components/GTDRTable';
import BasicImage from '@home/components/BasicImage';
import StudioAssessneRatingBar from '../StudioAssessneRatingBar';
import { DEFAULT_IMG } from '@/pages/dashboard/pages/benchmark/gtdrAssessment/components/GTDRRadarChart/utils';
import './index.scss';
import { StudioContext } from '../../../context';

/**
 * 根据给定的工作室名称修改工作室数据
 *
 * @param studio 给定的工作室名称
 * @returns 如果当前游戏的工作室代码与给定的工作室名称一致，则返回公司信息中的名称；否则返回给定的工作室名称
*/
export const changeStudioName = (studio: string, currentGame: any, companyInfo: any) => {
  return currentGame?.studio_code === studio ? companyInfo?.name : studio;
};

interface IStudioAssessnebtTableCardProps extends BaseComponentProps {
  defaultTab: string;
  currentTab: string;
  [key: string]: any;
}

// const version = ['CD Projekt Red 250314', 'techland 250313'];

const StudioAssessnebtTableCard = (props: IStudioAssessnebtTableCardProps) => {
  const {
    style,
    className,
    titleEn,
    titleCn,
    height = 1000,
    data,
    performanceData,
    performanceLoading,
    performanceError,
    filterValue,
    colorLists,
    // gameName,
    defaultTab = 'table',
    currentTab,
    benchmarkConfig,
    hasCompetitors
  } = props;

  const { commonData, gameCode } = data || {};
  const { data: gameData, errorMsg, loading } = commonData || {};
  const { themeValue } = useContext(ThemeContext);
  const { companyInfo, currentGame } = useContext(StudioContext);
  const isDark = themeValue === SYSTEM_THEME_DARK;
  const colorList = isDark ? colorLists?.dark : colorLists?.light;
  const benchmarkLevelMap = benchmarkConfig?.reduce((t, c) => {
    t[c?.benchmark_level] = c?.value;
    return t;
  }, {});

  const gameName = filterValue?.competitors?.map(item => item?.studio) || [];
  const version = filterValue?.competitors?.map(item => item?.value) || [];
  const gameCover = filterValue?.competitors?.map(item => item?.cover) || [];

  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;


  const title = getName(isEn, titleEn, titleCn);

  const [tableData, setTableData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const [expandAll, setExpandAll] = useState(false);



  useEffect(() => {
    if (currentTab) {
      setActiveTab(currentTab);
    }
  }, [currentTab]);
  
  useEffect(() => {
    setActiveTab(hasCompetitors ? defaultTab : 'bar');
  }, [hasCompetitors, defaultTab]);
  

  useEffect(() => {
    const newColumns: any = [
      {
        key: 'name',
        header: '',
        render: (value: string, row: any) => (
          <div className="dbd-benchmark-gtdr-table-content-td-div">
            <CommonBubble
              content={
                row?.name_desc && (
                  <FormatHtmlTextToReact text={row?.name_desc} />
                )
              }
            >
              {value}
            </CommonBubble>
          </div>
        ),
      },
    ];

    // table视图
    if (activeTab === 'table') {
      newColumns.push(
        ...(version || [])?.map((ver: string, index: number) => ({
          key: ver,
          header: getGameHeader({
            name: changeStudioName(gameName?.[index], currentGame, companyInfo), // 本品的话需要特殊处理
            version: ver?.split('-')?.[1],
            img: gameCover?.[index] || DEFAULT_IMG,
          }),
          render: (value: string, item: any) => {
            return getStudioScoreDom(
              value,
              { ...item, ...benchmarkLevelMap },
              colorList,
              `${ver}_desc`,
              isDark,
            );
          },
          canClose: version?.length > 1,
          isVersion: true,

        })),
      );
    } else {
      // 竖向展示的table
      newColumns.push({
        key: 'info',
        header: getName(isEn, 'Grade', '评级'),
        render: (value: string, item: any) =>
          getStudioMoreScoreDom(
            version,
            gameCover,
            { ...item, ...benchmarkLevelMap },
            colorList,
          ),
      });
      newColumns.push({
        key: 'desc',
        header: (
          <div className="dbh-benchmark-studio-table-card-desc-header">
            <div className="dbh-benchmark-studio-table-card-desc-header-title">
              <BasicImage src={gameCover?.[0] || DEFAULT_IMG} /> {getName(isEn, 'Description', '描述')}
            </div>
            <div
              className="dbh-benchmark-studio-table-card-desc-header-expand"
              onClick={() => setExpandAll(!expandAll)}
            >
              {expandAll
                ? getName(isEn, 'Collapse all', '收起全部')
                : getName(isEn, 'Expand all', '展开全部')}
            </div>
          </div>
        ),
        render: (value: string, item: any) => getStudioDescDom(item, expandAll, gameName?.[0]),
      });
    }
    setColumns(newColumns);
  }, [
    isEn,
    gameCode,
    colorList,
    loading,
    activeTab,
    expandAll,
  ]);


  useEffect(() => {
    const baseData: any = {};
    const mergeData = gameData || [];
    // 分类组装数据
    mergeData.forEach((item) => {
      const {
        category,
        category_cn,
        category_en,
        index,
        index_cn,
        index_en,
        value,
        version,
        description_cn,
        description_en,
        index_description_en,
        index_description_cn,
        studio,
      } = item;
      // 表格行数据
      const obj = {
        key: index,
        value,
        studio,
        name: getName(isEn, index_en, index_cn),
        version: `${changeStudioName(studio, currentGame, companyInfo)} - ${version}`, // 与filterValue的value对应
        nameDesc: getName(isEn, index_description_en, index_description_cn),
        valueDesc: getName(isEn, description_en, description_cn),
      };

      if (baseData[category]) {
        baseData[category].children.push(obj);
      } else {
        baseData[category] = {
          title: getName(isEn, category_en, category_cn),
          isOpen: true,
          children: [obj],
        };
      }
    });
    const list = Object.keys(baseData).map((key) => {
      const item = baseData[key] || {};
      const { children } = item;
      const childObj: any = {};

      children.forEach((child: any) => {
        const { key, value, name, version, nameDesc, valueDesc, studio } = child;
        // table视图
        if (activeTab === 'table') {
          // 根据version作为key值
          if (childObj[key]) {
            childObj[key][version] = value;
            childObj[key][`${version}_desc`] = valueDesc;
          } else {
            childObj[key] = {
              [version]: value,
              [`${version}_desc`]: valueDesc,
              name,
              name_desc: nameDesc,
            };
          }
        } else {
          // 横向table视图
          const studioItem = {
            name: version,
            value,
            studio,
            desc: valueDesc,
          };
          if (childObj[key]) {
            childObj[key].studioList?.push(studioItem);
          } else {
            childObj[key] = {
              name,
              name_desc: nameDesc,
              studioList: [studioItem],
            };
          }
        }
      });

      return {
        ...item,
        children: Object.keys(childObj).map((key) => childObj[key]),
      };
    });
    setTableData(list);
  }, [performanceData, data, isEn, gameData, activeTab]);

  const closeGame = (item: any) => {
    let columnList = columns.filter((col) => col?.key !== item?.key);
    if (columnList.length === 1) {
      columnList = columnList.map((item) => ({
        ...item,
        canClose: false,
      }));
    }
    setColumns(columnList);
  };

  const svgTypeList = [
    {
      svgType: 'sheet',
      value: 'table',
      component: (
        <GTDRTable
          columns={columns}
          data={tableData}
          setTableData={setTableData}
          closeColumn={closeGame}
        />
      ),
    },
    {
      svgType: 'hor-bar',
      value: 'bar',
      component: (
        <GTDRTable
          columns={columns}
          data={tableData}
          setTableData={setTableData}
        />
      ),
    },
  ];
  return (
    <CardTitleBox
      title={title}
      className={classNames(
        'dbh-benchmark-studio-table-card-container',
        {
          'is-hor-bar-view': activeTab !== 'table',
        },
        className,
      )}
      style={style}
      normalConfig={{
        isLoading: loading || performanceLoading,
        isError: !!errorMsg || performanceError,
        isNoData: gameData?.length === 0,
        style: { height },
      }}
      rightSolt={
        <div className="dbh-benchmark-studio-table-card-right">
          <div className="dbh-benchmark-studio-table-card-right-tool">
            {svgTypeList?.map((item) => {
              const { svgType, value } = item || {};
              return (
                <div
                  key={value}
                  className={classNames('dbh-benchmark-studio-icon', {
                    'dbh-benchmark-studio-active-icon': value === activeTab,
                  })}
                  onClick={() => setActiveTab(value)}
                >
                  <SvgIcon type={svgType} />
                </div>
              );
            })}
          </div>
          <StudioAssessneRatingBar
            colorList={colorList}
            colorTextList={[formatMessage({id: 'benchmark_table_high_risk'}), formatMessage({id: 'benchmark_table_medium_risk'}), formatMessage({id: 'benchmark_table_low_risk'})]}
            textList={['0', '3', '6', '10']}
            preText={formatMessage({id: 'benchmark_table_benchmark'})}
            style={{ margin: '20px 8px 20px 0' }}
          />
        </div>
      }
    >
      {svgTypeList.find((item) => item.value === activeTab)?.component}
    </CardTitleBox>
  );
};

export default StudioAssessnebtTableCard;
